import { useState, useEffect, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import classNames from "classnames";
import styles from "./styles.module.scss";
import { Message } from "shared/types/chatBoard";
import CustomPageHeading from "shared/components/common/customPageHeading";
import { Icons } from "assets";
import CustomTextEditor from "shared/components/common/customTextEditor";
import MessageItem from "shared/components/dashboardCommon/messageItem";
import ChatBoardCommentSkeleton from "shared/components/common/chatBoardCommentSkeleton";
import useChatBoardReplies from "shared/hooks/useChatBoardReplies";
import useChatBoardForms from "shared/hooks/useChatBoardForms";
import useChatBoardSocket from "shared/hooks/useChatBoardSocket";
import { mapReplyToMessage } from "shared/utils/chatBoardUtils";

const DesignerChatThread = () => {
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const auth = useSelector((state: any) => state.root.auth);
  const selectedMessage = location.state?.selectedMessage as Message | null;
  const chatBoardId = location.state?.chatBoardId as
    | string
    | number
    | undefined;
  const isReadOnly = location.state?.isReadOnly === true;
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const [, setMessages] = useState<Message[]>([]);

  const { replies, setReplies, loadingReplies, fetchReplies } =
    useChatBoardReplies({
      setMessages,
    });

  const { socketRef } = useChatBoardSocket({
    chatBoardId: chatBoardId || id,
    userId: auth?.user?.user_id,
    setMessages,
    setReplies,
    setSelectedMessage: () => {},
    messagesContainerRef,
  });

  const {
    replyMessage,
    setReplyMessage,
    replyFiles,
    isSubmittingReply,
    handleReplyFileSelect,
    handleRemoveReplyFile,
    handleSubmitReply,
  } = useChatBoardForms({
    chatBoardId: chatBoardId || id,
    auth,
    socketRef,
    messagesContainerRef,
    setMessages,
    selectedMessage,
    setSelectedMessage: () => {},
    setReplies,
  });

  useEffect(() => {
    if (
      selectedMessage &&
      (selectedMessage.repliesCount ?? 0) > 0 &&
      !replies[selectedMessage.id] &&
      !loadingReplies[selectedMessage.id]
    ) {
      fetchReplies(selectedMessage.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMessage?.id]);

  const getFileIcon = (type: string) => {
    if (type === "image") {
      return <Icons.TabImage />;
    }
    return <Icons.TabOverview />;
  };

  if (!selectedMessage) {
    return (
      <div>
        <CustomPageHeading
          title="Chat Thread"
          description="No message selected"
        />
      </div>
    );
  }

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.box}>
        <div className={styles.selectedMessageContainer}>
          <div className={styles.selectedMessageItem}>
            <MessageItem
              message={selectedMessage}
              variant="replyPanel"
              showReplyButton={false}
              getFileIcon={getFileIcon}
              removeBackgroundAndPadding={true}
            />
          </div>

          {(selectedMessage.repliesCount ?? 0) > 0 && (
            <div className={styles.repliesContainer}>
              {loadingReplies[selectedMessage.id] ? (
                <div
                  className={classNames(
                    styles.repliesList,
                    "flex flex-col gap-4",
                  )}
                >
                  <ChatBoardCommentSkeleton
                    count={3}
                    className={styles.replySkeleton}
                  />
                </div>
              ) : replies[selectedMessage.id] &&
                replies[selectedMessage.id].length > 0 ? (
                <div
                  className={classNames(
                    styles.repliesList,
                    "flex flex-col gap-4",
                  )}
                >
                  {replies[selectedMessage.id].map(
                    (reply: any, index: number) => {
                      const replyMessage = mapReplyToMessage(reply);
                      return (
                        <div key={reply.reply_id || reply.id || index}>
                          <MessageItem
                            message={replyMessage}
                            variant="default"
                            showReplyButton={false}
                            getFileIcon={getFileIcon}
                            removeBackgroundAndPadding={true}
                          />
                        </div>
                      );
                    },
                  )}
                </div>
              ) : null}
            </div>
          )}
        </div>
        <div
          className={classNames(
            styles.messageInputContainer,
            "fixed bottom-0 left-0 right-0",
          )}
        >
          <CustomTextEditor
            value={replyMessage}
            onChange={setReplyMessage}
            customContainerStyle={styles.commentsEditor}
            customToolbarStyle={styles.commentsToolbar}
            placeholder={isReadOnly ? "This project is completed. Chat is read-only." : "Type your reply here..."}
            onFileSelect={handleReplyFileSelect}
            onSend={handleSubmitReply}
            selectedFiles={replyFiles}
            onRemoveFile={handleRemoveReplyFile}
            isSubmitting={isSubmittingReply}
            showFileUpload={true}
            showSendButton={true}
            fileInputId="reply-file-input"
            disabled={isReadOnly}
          />
        </div>
      </div>
    </div>
  );
};

export default DesignerChatThread;
